<template>
  <section class="dealer-map-search">
    <div class="dealer-map-search__map">
      <span class="dealer-map-search__map-loader"/>
    </div>
    <dealer-list-pane
      class="dealer-map-search__shops"
      :shopList="enableShopLists"
      @search="searchLocation"
    />
  </section>
</template>

<script>
import {defineComponent, ref} from 'vue'
import axios from 'axios'
import {Loader} from '@googlemaps/js-api-loader'
import DealerListPane from './DealerMapSearch/DealerListPane.vue'

export default defineComponent({
  name: 'DealerMapSearch',
  components: {
    DealerListPane
  },
  setup() {
    const
      DEFAULT_LOCATION = {lat: 35.701589, lng: 139.709197},
      enabledMap = ref(false),
      userLocation = ref({lat: 0, lng: 0}),
      google = ref(null),
      map = ref(null),
      markers = ref([]),
      markerData = ref([]),
      infoWindows = ref([]),
      shopLists = ref([]),
      enableShopLists = ref([]),
      userPin = ref(null)

    return {
      DEFAULT_LOCATION,
      enabledMap,
      google,
      map,
      userPin,
      markers,
      markerData,
      infoWindows,
      shopLists,
      enableShopLists,
      userLocation,
    }
  },
  async mounted() {
    await this.mountGoogleMap()
    this.shopLists = await this.getShopLists()
    await this.filterShownShops(this.map.getBounds())

    await this.setMapMaker()

    this.map.addListener('idle', () => {
      this.filterShownShops(this.map.getBounds())
    })
  },
  methods: {
    /**
     * ショップリストの取得
     * @returns {Promise<void>}
     */
    async getShopLists() {
      const shopList = await axios
        .get(
          '/wp-json/wp/v2/shop/',
          {params: {per_page: 100}}
        )
        .then(response => response.data)
        .catch(error => {
          console.error(error)
          return []
        })

      // 全ショップマップマーカーデータを作成
      shopList.forEach(shop => {
        this.markerData.push({
          name: shop.title.rendered,
          address: `〒${shop.acf.zipcode}<br>${shop.acf.address}`,
          lat: Number(shop.acf.lat),
          lng: Number(shop.acf.lon),
          icon: '/wp-content/themes/HDJ2023_v1/assets/img/dealer_search/map-pin.svg',
          link: shop.link,
          distance: this.calculateDistance(
            Number(this.userLocation.lat),
            Number(this.userLocation.lng),
            Number(shop.acf.lat),
            Number(shop.acf.lon)
          ),
          mapsLink: `https://www.google.co.jp/maps/search/${encodeURI(shop.acf.address)}%C2%A0${encodeURI(shop.title.rendered)}?hl=ja`
        })
      })
      return shopList
    },
    /**
     * Google Mapのマウント
     */
    async mountGoogleMap() {
      const getCurrentPosition = (options) => {
        return new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, options)
        })
      }

      // 現在地取得
      if (navigator.geolocation) {
        const position = await getCurrentPosition({
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        }).catch(error => {
          switch (error.code) {
            case 1:
              alert('位置情報の利用が許可されていません')
              break;
            case 2:
              alert('現在位置が取得できませんでした')
              break;
            case 3:
              alert('タイムアウトになりました')
              break;
            default:
              alert(`その他のエラー(エラーコード:${error.code})`)
              break;
          }
          return {
            coords: {
              latitude: this.DEFAULT_LOCATION.lat,
              longitude: this.DEFAULT_LOCATION.lng
            }
          }
        })

        this.userLocation.lat = position.coords.latitude
        this.userLocation.lng = position.coords.longitude
      } else {
        this.userLocation.lat = this.DEFAULT_LOCATION.lat
        this.userLocation.lng = this.DEFAULT_LOCATION.lng
        console.error('Geolocation is not supported by this browser.')
      }

      /**
       * Google Maps APIの読み込み
       * TODO: APIキーを隠す
       */
      await new Loader(
        {
          apiKey: 'AIzaSyAADhVgHgz8m0av9skKjuvpOW2zEGU0x-s',
          version: 'weekly',
          libraries: ['places']
        }
      )
        .importLibrary('maps')
        .then(({Map}) => {
          this.google = Map
          this.map = new Map(
            document.querySelector('.dealer-map-search__map'),
            {
              center: {
                lat: this.userLocation.lat,
                lng: this.userLocation.lng
              },
              zoom: 11,
              scaleControl: true,
            })
        })
        .catch((e) => {
          console.error(e)
        })
    },
    /**
     * 表示領域内のショップリストを現在地距離順でセット
     * @param bounds
     */
    filterShownShops(bounds) {
      const viewGps = {
        swlat: bounds.getSouthWest().lat(),
        swlng: bounds.getSouthWest().lng(),
        nelat: bounds.getNorthEast().lat(),
        nelng: bounds.getNorthEast().lng(),
      }
      const tempList = []
      this.shopLists.forEach(shop => {
        if (
          viewGps.swlat < Number(shop.acf.lat) &&
          viewGps.swlng < Number(shop.acf.lon) &&
          Number(shop.acf.lat) < viewGps.nelat &&
          Number(shop.acf.lon) < viewGps.nelng
        ) {
          tempList.push(this.getMapsShopData(shop))
        }
      })
      tempList.sort((a, b) => Number(a.distance) - Number(b.distance))
      this.enableShopLists = tempList
    },
    /**
     * 使用するショップデータのみ返却
     * @param shop
     * @returns {{zipcode, address: (string|string|*), name: (boolean|*), link, lon, tel: (boolean|*), end: *, id, fax: (string|*), slug, lat: (number|string|*), open}}
     */
    getMapsShopData(shop) {
      return {
        id: shop.id,
        name: shop.title.rendered,
        slug: shop.slug,
        link: shop.link,
        lat: shop.acf.lat,
        lon: shop.acf.lon,
        zipcode: shop.acf.zipcode,
        address: shop.acf.address,
        tel: shop.acf.tel,
        fax: shop.acf.fax,
        open: shop.acf.open,
        end: shop.acf.open_end,
        distance: this.calculateDistance(
          Number(this.userLocation.lat),
          Number(this.userLocation.lng),
          Number(shop.acf.lat),
          Number(shop.acf.lon)
        ),
      }
    },
    /**
     * マップマーカーのセット
     */
    async setMapMaker() {
      await this.markerData.forEach((maker, i) => {
        this.markers[i] = new window.google.maps.Marker({
          position: {
            lat: maker.lat,
            lng: maker.lng
          },
          icon: {url: maker.icon},
          map: this.map
        })


        this.infoWindows[i] = new window.google.maps.InfoWindow({
          content: this.getInfoWindowContent(maker)
        })

        this.markers[i].addListener('click', () => {
          this.infoWindows[i].open(this.map, this.markers[i])
        })
      })

      this.userPin = new google.maps.Marker({
        position: {
          lat: Number(this.userLocation.lat),
          lng: Number(this.userLocation.lng),
        },
        map: this.map,
      })
    },
    /**
     * インフォウィンドウ情報を更新
     * @returns {Promise<void>}
     */
    async resetInfoWindowContent(){
      await this.markerData.forEach((maker, i) => {
        this.infoWindows[i].setContent(this.getInfoWindowContent(maker))
      })
    },
    /**
     * インフォウィンドウ情報を返却
     * @param maker
     * @returns {string}
     */
    getInfoWindowContent(maker){
      const name = `<h6>${maker['name']}</h6>`
      const address = `${maker['address']}<br>`
      const tel = maker['tel'] ? `<a href="tel:${maker['tel']}">${maker['tel']}</a>` : ''
      const fax = maker['fax'] ? ` / ${maker['fax']}` : ''
      const link = `<li><a href="${maker['link']}" target="_blank" rel="noopener noreferrer">販売店詳細</a></li>`
      const mapLink = `<li><a href="${maker['mapsLink']}" target="_blank" rel="noopener noreferrer">Google Mapを拡大</a></li>`

      return `
        <article class="dealer-map-search__info">
            ${name}
            <p>${address}${tel}${fax}</p>
            <p>店舗まで約${maker.distance}km</p>
            <ul>${link}${mapLink}</ul>
        </article>`
    },
    /**
     * 住所検索
     * @param location
     */
    async searchLocation(location) {
      const map = this.map
      const geocoder = new window.google.maps.Geocoder()
      let newLatLng = null

      if (geocoder) {
        await geocoder.geocode({address: location}, function (results, status) {
          if (status === 'OK') {
            map.zoom = 11
            map.setCenter(results[0].geometry.location)
            newLatLng = new google.maps.LatLng(results[0].geometry.location.lat(), results[0].geometry.location.lng())
          } else {
            alert('該当する住所が見つかりませんでした')
          }
        })

        if (newLatLng) {
          this.userPin.setPosition(newLatLng)
          this.userLocation.lat = newLatLng.lat()
          this.userLocation.lng = newLatLng.lng()
          await this.resetDistance()
          await this.resetInfoWindowContent()
          await this.filterShownShops(this.map.getBounds())
        }
      }
    },
    /**
     * ユーザーの現在地との距離を取得
     * 両指定地の経度緯度情報から距離を計算
     */
    calculateDistance(lat1, lon1, lat2, lon2) {
      const R = 6371
      const deg2rad = (deg) => deg * (Math.PI / 180)
      const dLat = deg2rad(lat2 - lat1)
      const dLon = deg2rad(lon2 - lon1)

      const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) *
        Math.sin(dLon / 2) * Math.sin(dLon / 2)
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
      return (R * c).toFixed(1)
    },
    /**
     * ユーザーの現在地との距離を再取得
     */
    resetDistance() {
      this.shopLists.forEach((shop, idx) => {
        this.shopLists[idx].distance = this.calculateDistance(
          Number(this.userLocation.lat),
          Number(this.userLocation.lng),
          Number(shop.lat),
          Number(shop.lon)
        )
      })

      this.markerData.forEach((maker, idx) => {
        this.markerData[idx].distance = this.calculateDistance(
          Number(this.userLocation.lat),
          Number(this.userLocation.lng),
          Number(maker.lat),
          Number(maker.lng)
        )
      })
    },
  }
})

</script>